import {
  useCallback,
  useEffect,
  useMemo,
  useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import Autocomplete from './SearchableAutocomplete';
import TextField from './SearchableTextField';
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { getAllClienti, getAllCompanies } from "../APIs/ClientsAPI";
import { getAllPackages } from "../APIs/PackagesAPI";
import { getAllProdotti } from "../APIs/ProductsAPI";
import { getAllTavoli } from "../APIs/TablesAPI";
import {
  createReservationEvent,
  deleteReservationEvent,
  getReservationEvents,
  patchReservationEventStatus,
} from "../APIs/ReservationEventsAPI";
import { inviaPreventivoEmail } from "../APIs/PreventiviAPI";
import { localizedErrorMessage } from "../utils/italianErrorMessage";
import { getTenantTranslation } from "../utils/getTenantTranslation";
import DateTimeLocal24Field from "./DateTimeLocal24Field";

const EMPTY_DAY = {
  startDate: "",
  endDate: "",
  guests: "",
  tableIds: [],
  packageIds: [],
  serviceIds: [],
  note: "",
};

const formatDateTime24 = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("it-IT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

function toFloat(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function toIsoFromLocalDateTime(value) {
  if (!value || typeof value !== "string") return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export default function EventReservationPanel({ t, showSnackbar, onDataChanged, refreshToken = 0 }) {
  const ep = useCallback((key, options) => t(`eventPanel.${key}`, options), [t]);
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const tenantType = localStorage.getItem("tenantType");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [eventName, setEventName] = useState("");
  const [status, setStatus] = useState("PREVENTIVO");
  const [note, setNote] = useState("");
  const [recipientMode, setRecipientMode] = useState("company");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [days, setDays] = useState([EMPTY_DAY]);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [tables, setTables] = useState([]);
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [sending, setSending] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  const [sendQuote, setSendQuote] = useState(false);
  const [quoteEmail, setQuoteEmail] = useState("");
  const [quoteSubject, setQuoteSubject] = useState("");
  const [quoteMessage, setQuoteMessage] = useState("");

  useEffect(() => {
    async function loadAll() {
      setLoading(true);
      setError("");
      try {
        const [c, co, ta, pk, sv, ev] = await Promise.all([
          getAllClienti(token, tenant),
          getAllCompanies(token, tenant),
          getAllTavoli(token, tenant),
          getAllPackages(token, tenant),
          getAllProdotti(token, tenant),
          getReservationEvents(token, tenant),
        ]);
        setClients(Array.isArray(c) ? c : []);
        setCompanies(Array.isArray(co) ? co : []);
        setTables(Array.isArray(ta) ? ta : []);
        setPackages(Array.isArray(pk) ? pk : []);
        setServices(Array.isArray(sv) ? sv : []);
        setRecentEvents(Array.isArray(ev) ? ev.slice(0, 5) : []);
      } catch (err) {
        setError(localizedErrorMessage(err, ep("loadError")));
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, [token, tenant, refreshToken, ep]);

  useEffect(() => {
    if (recipientMode === "company") {
      setQuoteEmail(selectedCompany?.email || "");
    } else {
      setQuoteEmail(selectedClient?.email || "");
    }
  }, [recipientMode, selectedClient, selectedCompany]);

  const packageById = useMemo(
    () => Object.fromEntries(packages.map((item) => [item.id, item])),
    [packages],
  );
  const serviceById = useMemo(
    () => Object.fromEntries(services.map((item) => [item.id, item])),
    [services],
  );

  const handleDayChange = (index, key, value) => {
    setDays((prev) => prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  };

  const addDay = () => setDays((prev) => [...prev, { ...EMPTY_DAY }]);
  const removeDay = (index) => setDays((prev) => prev.filter((_, i) => i !== index));

  const validate = () => {
    if (!eventName.trim()) return ep("eventNameRequired");
    if (recipientMode === "company" && !selectedCompany?.id) return ep("selectCompany");
    if (recipientMode === "client" && !selectedClient?.id) return ep("selectClient");
    if (!Array.isArray(days) || days.length === 0) return ep("oneDayRequired");
    for (const day of days) {
      if (!day.startDate || !day.endDate) return ep("dayNeedsStartEnd");
      if (new Date(day.endDate) <= new Date(day.startDate)) return ep("dayEndAfterStart");
      if (!Number.isInteger(Number(day.guests)) || Number(day.guests) <= 0) return ep("invalidGuests");
      if (status !== "PREVENTIVO" && (!Array.isArray(day.tableIds) || day.tableIds.length === 0)) {
        return getTenantTranslation(t, tenantType, "eventPanel.tableRequiredWhenConfirmed");
      }
    }
    if (sendQuote && !quoteEmail.includes("@")) return ep("invalidQuoteEmail");
    return "";
  };

  const buildQuoteItems = () => {
    const items = [];
    days.forEach((day, idx) => {
      day.packageIds.forEach((packageId) => {
        const pkg = packageById[packageId];
        if (!pkg) return;
        items.push({
          name: `[${ep("dayPrefix", { number: idx + 1 })}] ${pkg.name}`,
          unit: ep("packageUnit"),
          qty: 1,
          unitPrice: toFloat(pkg.price).toFixed(2),
          total: toFloat(pkg.price).toFixed(2),
        });
      });
      day.serviceIds.forEach((productId) => {
        const svc = serviceById[productId];
        if (!svc) return;
        items.push({
          name: `[${ep("dayPrefix", { number: idx + 1 })}] ${svc.name}`,
          unit: ep("serviceUnit"),
          qty: 1,
          unitPrice: toFloat(svc.price).toFixed(2),
          total: toFloat(svc.price).toFixed(2),
        });
      });
    });
    return items;
  };

  const handleSubmit = async () => {
    const validationMessage = validate();
    if (validationMessage) {
      showSnackbar(validationMessage, "error");
      return;
    }

    setSending(true);
    try {
      const payload = {
        eventName: eventName.trim(),
        status,
        note: note.trim() || undefined,
        clientId: recipientMode === "client" ? selectedClient.id : undefined,
        companyId: recipientMode === "company" ? selectedCompany.id : undefined,
        days: days.map((day) => ({
          dayDate: toIsoFromLocalDateTime(day.startDate),
          startDate: toIsoFromLocalDateTime(day.startDate),
          endDate: toIsoFromLocalDateTime(day.endDate),
          guests: Number(day.guests),
          note: day.note?.trim() || undefined,
          tableIds: day.tableIds.map(Number),
          packageItems: day.packageIds.map((packageId) => {
            const pkg = packageById[packageId];
            return {
              packageId: Number(packageId),
              quantity: 1,
              unitPrice: toFloat(pkg?.price || 0),
            };
          }),
          serviceItems: day.serviceIds.map((productId) => {
            const svc = serviceById[productId];
            return {
              productId: Number(productId),
              quantity: 1,
              unitPrice: toFloat(svc?.price || 0),
            };
          }),
        })),
      };

      const created = await createReservationEvent(payload, token, tenant);

      if (sendQuote) {
        const quoteItems = buildQuoteItems();
        const total = quoteItems.reduce((sum, item) => sum + toFloat(item.total), 0);
        await inviaPreventivoEmail({
          to: quoteEmail,
          clientName: recipientMode === "company"
            ? selectedCompany?.companyName
            : `${selectedClient?.name || ""} ${selectedClient?.surname || ""}`.trim(),
          subject: quoteSubject || ep("quoteSubject", { eventName: eventName.trim() }),
          message: quoteMessage || "",
          eventDate: days[0]?.startDate ? new Date(days[0].startDate).toLocaleDateString() : "",
          validUntil: "",
          items: quoteItems,
          total: total.toFixed(2),
        }, token, tenant);
      }

      const updatedEvents = await getReservationEvents(token, tenant);
      setRecentEvents(Array.isArray(updatedEvents) ? updatedEvents.slice(0, 5) : []);
      if (typeof onDataChanged === "function") await onDataChanged();
      showSnackbar(ep("eventCreated", { name: created.eventName, status: created.status }), "success");
      setEventName("");
      setStatus("PREVENTIVO");
      setNote("");
      setDays([EMPTY_DAY]);
      setSendQuote(false);
      setQuoteSubject("");
      setQuoteMessage("");
    } catch (err) {
      showSnackbar(err?.message || ep("createError"), "error");
    } finally {
      setSending(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm(ep("deleteConfirm"))) return;
    setUpdatingStatusId(eventId);
    try {
      await deleteReservationEvent(eventId, token, tenant);
      const updatedEvents = await getReservationEvents(token, tenant);
      setRecentEvents(Array.isArray(updatedEvents) ? updatedEvents.slice(0, 5) : []);
      if (typeof onDataChanged === "function") await onDataChanged();
      showSnackbar(ep("deleted"), "success");
    } catch (err) {
      showSnackbar(err?.message || ep("deleteError"), "error");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleChangeEventStatus = async (eventId, nextStatus) => {
    setUpdatingStatusId(eventId);
    try {
      await patchReservationEventStatus(eventId, nextStatus, token, tenant);
      const updatedEvents = await getReservationEvents(token, tenant);
      setRecentEvents(Array.isArray(updatedEvents) ? updatedEvents.slice(0, 5) : []);
      if (typeof onDataChanged === "function") await onDataChanged();
      showSnackbar(ep("statusUpdated", { status: nextStatus }), "success");
    } catch (err) {
      showSnackbar(err?.message || ep("statusUpdateError"), "error");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const formatEventPeriod = (event) => {
    const starts = (event?.days || []).map((d) => new Date(d?.startDate)).filter((d) => !Number.isNaN(d.getTime()));
    const ends = (event?.days || []).map((d) => new Date(d?.endDate)).filter((d) => !Number.isNaN(d.getTime()));
    if (starts.length === 0 || ends.length === 0) return "-";
    const minStart = new Date(Math.min(...starts.map((d) => d.getTime())));
    const maxEnd = new Date(Math.max(...ends.map((d) => d.getTime())));
    return `${formatDateTime24(minStart)} - ${formatDateTime24(maxEnd)}`;
  };

  const countEventGuests = (event) =>
    (event?.days || []).reduce((sum, day) => sum + Number(day?.guests || 0), 0);

  const getEventDayStatuses = (event) => {
    const statuses = Array.from(new Set(
      (event?.days || [])
        .map((day) => day?.legacyReservation?.status)
        .filter(Boolean),
    ));
    return statuses.length > 0 ? statuses.join(", ") : "-";
  };

  const getEventRecipient = (event) =>
    event.company?.companyName || `${event.client?.name || ""} ${event.client?.surname || ""}`.trim() || "-";

  const renderEventActions = (event) => (
    <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }} flexWrap="wrap" useFlexGap>
      {event.status !== "PREVENTIVO" && (
        <Button
          size="small"
          variant="outlined"
          disabled={updatingStatusId === event.id}
          onClick={() => handleChangeEventStatus(event.id, "PREVENTIVO")}
        >
          PREVENTIVO
        </Button>
      )}
      {event.status !== "TO_CONFIRM" && (
        <Button
          size="small"
          variant="outlined"
          disabled={updatingStatusId === event.id}
          onClick={() => handleChangeEventStatus(event.id, "TO_CONFIRM")}
        >
          TO_CONFIRM
        </Button>
      )}
      {event.status !== "CONFIRMED" && (
        <Button
          size="small"
          variant="contained"
          disabled={updatingStatusId === event.id}
          onClick={() => handleChangeEventStatus(event.id, "CONFIRMED")}
        >
          {ep("confirm")}
        </Button>
      )}
      <Button
        size="small"
        color="error"
        variant="outlined"
        disabled={updatingStatusId === event.id}
        onClick={() => handleDeleteEvent(event.id)}
      >
        {ep("delete")}
      </Button>
    </Stack>
  );

  return (
    <Card variant="outlined" sx={{ mb: 3, maxWidth: "100%", overflow: "hidden" }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
          {ep("title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {ep("subtitle")}
        </Typography>

        {loading && <Alert severity="info">{ep("loading")}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label={ep("eventName")}
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              label={ep("initialStatus")}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="PREVENTIVO">PREVENTIVO</MenuItem>
              <MenuItem value="TO_CONFIRM">TO_CONFIRM</MenuItem>
              <MenuItem value="CONFIRMED">CONFIRMED</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label={ep("recipient")}
              value={recipientMode}
              onChange={(e) => setRecipientMode(e.target.value)}
            >
              <MenuItem value="company">{ep("company")}</MenuItem>
              <MenuItem value="client">{ep("client")}</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            {recipientMode === "company" ? (
              <Autocomplete
                options={companies}
                getOptionLabel={(o) => `${o.companyName}${o.email ? ` - ${o.email}` : ""}`}
                value={selectedCompany}
                onChange={(_, val) => setSelectedCompany(val)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => <TextField {...params} label={ep("company")} />}
              />
            ) : (
              <Autocomplete
                options={clients}
                getOptionLabel={(o) => `${o.name} ${o.surname}${o.email ? ` - ${o.email}` : ""}`}
                value={selectedClient}
                onChange={(_, val) => setSelectedClient(val)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => <TextField {...params} label={ep("client")} />}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={ep("eventNotes")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Grid>
        </Grid>

        <Stack spacing={2}>
          {days.map((day, index) => (
            <Paper key={`day-${index}`} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {ep("day")} {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeDay(index)}
                  disabled={days.length === 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <DateTimeLocal24Field
                    label={ep("start")}
                    value={day.startDate}
                    onChange={(value) => handleDayChange(index, "startDate", value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DateTimeLocal24Field
                    label={ep("end")}
                    value={day.endDate}
                    onChange={(value) => handleDayChange(index, "endDate", value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label={ep("people")}
                    value={day.guests}
                    onChange={(e) => handleDayChange(index, "guests", e.target.value)}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={ep("dayNote")}
                    value={day.note}
                    onChange={(e) => handleDayChange(index, "note", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Autocomplete
                    multiple
                    options={tables}
                    getOptionLabel={(o) => o.name}
                    value={tables.filter((item) => day.tableIds.includes(item.id))}
                    onChange={(_, values) => handleDayChange(index, "tableIds", values.map((v) => v.id))}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={(params) => (
                      <TextField {...params} label={getTenantTranslation(t, tenantType, "eventPanel.tables")} />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Autocomplete
                    multiple
                    options={packages}
                    getOptionLabel={(o) => `${o.name} (${toFloat(o.price).toFixed(2)} EUR)`}
                    value={packages.filter((item) => day.packageIds.includes(item.id))}
                    onChange={(_, values) => handleDayChange(index, "packageIds", values.map((v) => v.id))}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={(params) => <TextField {...params} label={ep("packages")} />}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Autocomplete
                    multiple
                    options={services}
                    getOptionLabel={(o) => `${o.name} (${toFloat(o.price).toFixed(2)} EUR)`}
                    value={services.filter((item) => day.serviceIds.includes(item.id))}
                    onChange={(_, values) => handleDayChange(index, "serviceIds", values.map((v) => v.id))}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={(params) => <TextField {...params} label={ep("singleServices")} />}
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>

        <Button
          sx={{ mt: 1.5 }}
          startIcon={<AddIcon />}
          onClick={addDay}
          variant="outlined"
        >
          {ep("addDay")}
        </Button>

        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={sendQuote} onChange={(e) => setSendQuote(e.target.checked)} />}
            label={ep("sendQuoteAfterCreate")}
          />
          {sendQuote && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={ep("recipientEmail")}
                  value={quoteEmail}
                  onChange={(e) => setQuoteEmail(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={ep("subject")}
                  value={quoteSubject}
                  onChange={(e) => setQuoteSubject(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={ep("message")}
                  value={quoteMessage}
                  onChange={(e) => setQuoteMessage(e.target.value)}
                />
              </Grid>
            </Grid>
          )}
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button variant="contained" onClick={handleSubmit} disabled={sending || loading}>
            {sending ? ep("saving") : ep("createEvent")}
          </Button>
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 700 }}>
          {ep("latestEvents")}
        </Typography>
        <Stack spacing={1.25} sx={{ display: { xs: "flex", md: "none" } }}>
          {recentEvents.map((event) => (
            <Paper key={event.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, minWidth: 0 }}>
              <Stack spacing={1}>
                <Box>
                  <Typography fontWeight={800} sx={{ overflowWrap: "anywhere" }}>
                    {event.eventName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {event.status}
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">{ep("dayStatuses")}</Typography>
                    <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{getEventDayStatuses(event)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">{ep("recipient")}</Typography>
                    <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{getEventRecipient(event)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">{ep("period")}</Typography>
                    <Typography variant="body2">{formatEventPeriod(event)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">{ep("guests")}</Typography>
                    <Typography variant="body2">{countEventGuests(event)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">{ep("days")}</Typography>
                    <Typography variant="body2">{event.days?.length || 0}</Typography>
                  </Grid>
                </Grid>
                {renderEventActions(event)}
              </Stack>
            </Paper>
          ))}
          {recentEvents.length === 0 && (
            <Typography color="text.secondary">{ep("noEvents")}</Typography>
          )}
        </Stack>

        <Box sx={{ display: { xs: "none", md: "block" }, maxWidth: "100%", overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{ep("eventName")}</TableCell>
                <TableCell>{t("reports.status")}</TableCell>
                <TableCell>{ep("dayStatuses")}</TableCell>
                <TableCell>{ep("recipient")}</TableCell>
                <TableCell>{ep("period")}</TableCell>
                <TableCell align="right">{ep("guests")}</TableCell>
                <TableCell align="right">{ep("days")}</TableCell>
                <TableCell align="right">{ep("actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentEvents.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>{event.eventName}</TableCell>
                  <TableCell>{event.status}</TableCell>
                  <TableCell>{getEventDayStatuses(event)}</TableCell>
                  <TableCell>{getEventRecipient(event)}</TableCell>
                  <TableCell>{formatEventPeriod(event)}</TableCell>
                  <TableCell align="right">{countEventGuests(event)}</TableCell>
                  <TableCell align="right">{event.days?.length || 0}</TableCell>
                  <TableCell align="right">{renderEventActions(event)}</TableCell>
                </TableRow>
              ))}
              {recentEvents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>{ep("noEvents")}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
}
